import React, { useEffect, useState } from 'react';
import { settings as settingsApi } from '@/utils/api';
import { Save, Zap, Check, Eye, EyeOff, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import AgencyEmailPanel from './AgencyEmailPanel';

export default function IntegrationsPanel() {
  const [form, setForm] = useState({
    claude_api_key: '', stripe_secret_key: '', stripe_publishable_key: '', stripe_webhook_secret: '',
    paypal_client_id: '', paypal_client_secret: '',
    razorpay_key_id: '', razorpay_key_secret: '',
    google_client_id: '', google_client_secret: '',
  });
  const [currentSettings, setCurrentSettings] = useState({});
  const [showKey, setShowKey] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi.get().then(r => setCurrentSettings(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v.trim()) payload[k] = v.trim(); });
      await settingsApi.update(payload);
      await settingsApi.get().then(r => setCurrentSettings(r.data));
      setForm({ claude_api_key: '', stripe_secret_key: '', stripe_publishable_key: '', paypal_client_id: '', paypal_client_secret: '', razorpay_key_id: '', razorpay_key_secret: '' });
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const KeyField = ({ name, label, placeholder }) => (
    <div>
      <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>{label}</label>
      {currentSettings[`${name}_set`] && (
        <p className="text-xs mb-1 flex items-center gap-1" style={{ color: '#3C8A5A' }}>
          <Check size={11} /> Currently set: {currentSettings[name]}
        </p>
      )}
      <div className="relative">
        <input
          className="input-agency text-sm pr-10 font-mono"
          type={showKey[name] ? 'text' : 'password'}
          placeholder={placeholder}
          value={form[name]}
          onChange={e => setForm({ ...form, [name]: e.target.value })}
          data-testid={`integration-${name}`}
        />
        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowKey(s => ({...s, [name]: !s[name]}))}>
          {showKey[name] ? <EyeOff size={14} style={{ color: 'var(--muted)' }} /> : <Eye size={14} style={{ color: 'var(--muted)' }} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <h2 className="font-bold" style={{ color: 'var(--ink)' }}>Integrations</h2>

      <div className="card-base p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} style={{ color: '#006080' }} />
          <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>App Branding</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>App Name</label>
            <input
              className="input-agency text-sm"
              placeholder="Krris OS"
              value={form.app_name || ''}
              onChange={e => setForm(s => ({ ...s, app_name: e.target.value }))}
              data-testid="app-name-input"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Current: <b>{currentSettings.app_name || 'Krris OS'}</b></p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Primary Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                className="w-10 h-10 rounded-btn border cursor-pointer"
                style={{ borderColor: 'var(--border)' }}
                value={form.app_primary_color || currentSettings.app_primary_color || '#006080'}
                onChange={e => setForm(s => ({ ...s, app_primary_color: e.target.value }))}
                data-testid="app-primary-color-input"
              />
              <input
                className="input-agency text-sm font-mono flex-1"
                value={form.app_primary_color || ''}
                onChange={e => setForm(s => ({ ...s, app_primary_color: e.target.value }))}
                placeholder="#006080"
              />
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Tagline</label>
            <input
              className="input-agency text-sm"
              placeholder="Your agency on autopilot."
              value={form.app_tagline || ''}
              onChange={e => setForm(s => ({ ...s, app_tagline: e.target.value }))}
            />
          </div>
        </div>
        <p className="text-xs" style={{ color: 'var(--muted)' }}>These appear in the sidebar, login, signup, and public landing pages. Color changes apply everywhere.</p>
      </div>

      <div className="card-base p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Zap size={16} style={{ color: '#006080' }} />
          <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Anthropic Claude</h3>
        </div>
        <KeyField name="claude_api_key" label="Claude API Key" placeholder="sk-ant-..." />
        <p className="text-xs" style={{ color: 'var(--muted)' }}>Used for AI briefs, estimates, and skill runs. Get your key from console.anthropic.com</p>
      </div>

      <div className="card-base p-4 space-y-3">
        <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Stripe</h3>
        <KeyField name="stripe_secret_key" label="Secret Key" placeholder="sk_live_..." />
        <KeyField name="stripe_publishable_key" label="Publishable Key" placeholder="pk_live_..." />
        <KeyField name="stripe_webhook_secret" label="Webhook Signing Secret" placeholder="whsec_..." />
      </div>

      <div className="card-base p-4 space-y-3">
        <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>PayPal</h3>
        <KeyField name="paypal_client_id" label="Client ID" placeholder="AYSq3..." />
        <KeyField name="paypal_client_secret" label="Client Secret" placeholder="EEo..." />
      </div>

      <div className="card-base p-4 space-y-3">
        <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Razorpay</h3>
        <KeyField name="razorpay_key_id" label="Key ID" placeholder="rzp_live_..." />
        <KeyField name="razorpay_key_secret" label="Key Secret" placeholder="..." />
      </div>

      <div className="card-base p-4 space-y-3">
        <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Google Calendar OAuth</h3>
        <KeyField name="google_client_id" label="Google Client ID" placeholder="...apps.googleusercontent.com" />
        <KeyField name="google_client_secret" label="Google Client Secret" placeholder="GOCSPX-..." />
        <p className="text-xs" style={{ color: 'var(--muted)' }}>Authorized Redirect URI: <code>{window.location.origin}/api/calendar/oauth/callback</code>. Once keys saved, connect your Google account from the Calendar page.</p>
      </div>

      <AgencyEmailPanel currentSettings={currentSettings} reload={() => settingsApi.get().then(r => setCurrentSettings(r.data))} />

      <button
        className="btn-primary flex items-center gap-2 px-4 py-2"
        onClick={handleSave}
        disabled={saving}
        data-testid="save-integrations-button"
      >
        <Save size={14} /> {saving ? 'Saving...' : 'Save All Keys'}
      </button>
    </div>
  );
}
